import React, { useState, useEffect, useRef } from 'react';
import { db, handleFirestoreError, OperationType, isQuotaError } from '../firebase';
import { collection, addDoc, serverTimestamp, getDocs, query, where, orderBy, onSnapshot, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { getHolidays, getHolidayForDate } from '../services/holidayService';
import { syncRegularToHoliday } from '../services/attendanceSyncService';
import { refreshDailyStats } from '../services/statsService';
import { Save, Loader2, Trash2, UserX, Calendar, FileText, UserCheck, Search, ClipboardCheck, History, CheckCircle2, XCircle, FileCheck, ChevronRight, ChevronLeft, Calendar as CalendarIcon, AlertTriangle, RefreshCw, GraduationCap } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getCachedStaff, getCachedQuery } from '../services/dataCacheService';
import { StaffMember, StaffAbsence as StaffAbsenceType } from '../types';
import { useAcademicYear } from '../context/AcademicYearContext';
import { 
  getAcademicYearRange, 
  getAcademicYearForDate, 
  isAbsenceInAcademicYear 
} from '../services/academicYearService';
import { DEFAULT_ABSENCE_REASONS } from '../services/absenceReasonService';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FileDown, FileText as FileTextIcon } from 'lucide-react';

interface StaffAbsenceProps {
  type: 'educational' | 'assistant';
}

const StaffAbsence: React.FC<StaffAbsenceProps> = ({ type }) => {
  const { selectedYear, currentYearObj, academicYears, setSelectedYear } = useAcademicYear();
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [formData, setFormData] = useState({
    id: '',
    staffId: '',
    staffName: '',
    nationalId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    reason: '',
    substitute: '',
    substituteHours: '',
    notes: '',
    hasCertificate: false,
    certificateType: ''
  });
  const [absences, setAbsences] = useState<StaffAbsenceType[]>([]);
  const [substitutes, setSubstitutes] = useState<{id: string, name: string}[]>([]);
  const [viewMode, setViewMode] = useState<'report' | 'daily' | 'history'>('report');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isWeekend, setIsWeekend] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [reasons, setReasons] = useState<string[]>([]);
  const [autoSaving, setAutoSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const triggerAutoSave = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    
    // Only auto-save if we have at least a staff selected and dates
    if (!formData.staffId || !formData.startDate || !formData.reason) return;
    
    setAutoSaving(true);
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        const yearForDate = getAcademicYearForDate(formData.startDate) || selectedYear;
        const dataToSave = {
          ...formData,
          academicYear: yearForDate,
          substituteHours: formData.substituteHours ? Number(formData.substituteHours) : null,
          staffType: type,
          timestamp: serverTimestamp()
        };

        if (formData.id) {
          const { id, ...updateData } = dataToSave;
          await updateDoc(doc(db, 'staffAbsences', id), updateData);
        } else {
          const { id, ...addData } = dataToSave;
          const newDoc = await addDoc(collection(db, 'staffAbsences'), addData);
          setFormData(prev => ({ ...prev, id: newDoc.id }));
        }
        
        await refreshDailyStats(formData.startDate);
        if (formData.endDate !== formData.startDate) {
          await refreshDailyStats(formData.endDate);
        }
      } catch (error) {
        console.error("Auto-save error:", error);
      } finally {
        setAutoSaving(false);
      }
    }, 1500); // 1.5 second debounce for form
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const toggleDayAbsence = async (staff: StaffMember) => {
    const existingAbsence = absences.find(a => 
      a.staffId === staff.id && 
      selectedDate >= a.startDate && 
      selectedDate <= a.endDate
    );

    if (existingAbsence) {
      // Toggle to Present: Delete the absence record
      // For toggle, we skip the confirmation to make it fast
      await handleDeleteAbsence(existingAbsence.id!, true);
    } else {
      // Toggle to Absent: Create a quick absence record for today
      try {
        setSaving(true);
        const yearForDate = getAcademicYearForDate(selectedDate) || selectedYear;
        const dataToSave = {
          staffId: staff.id,
          staffName: staff.fullName,
          nationalId: staff.nationalId,
          startDate: selectedDate,
          endDate: selectedDate,
          academicYear: yearForDate,
          reason: 'היעדרות יומית',
          substitute: '',
          substituteHours: null,
          notes: '',
          hasCertificate: false,
          certificateType: '',
          staffType: type,
          timestamp: serverTimestamp()
        };
        await addDoc(collection(db, 'staffAbsences'), dataToSave);
        await refreshDailyStats(selectedDate);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'staffAbsences');
      } finally {
        setSaving(false);
      }
    }
  };
  
  // Filtering states for history - initialized with the active academic year
  const [historyFilters, setHistoryFilters] = useState({
    academicYear: selectedYear,
    staffName: '',
    startDate: '',
    endDate: '',
    certificateStatus: 'all' as 'all' | 'submitted' | 'pending'
  });

  // Keep history filter in sync when selectedYear changes in global context
  useEffect(() => {
    setHistoryFilters(prev => ({
      ...prev,
      academicYear: selectedYear
    }));
  }, [selectedYear]);

  // Ensure selectedDate defaults nicely within the active school year
  useEffect(() => {
    const range = getAcademicYearRange(selectedYear);
    const today = new Date().toISOString().split('T')[0];
    if (today >= range.startDate && today <= range.endDate) {
      setSelectedDate(today);
    } else {
      setSelectedDate(range.startDate);
    }
  }, [selectedYear]);

  // Compute filtered absences cleanly for desktop, mobile and exports
  const filteredAbsences = React.useMemo(() => {
    return absences.filter(a => {
      // 1. Filter by academic year (strictly 1.9 to 31.8)
      if (historyFilters.academicYear && historyFilters.academicYear !== 'all') {
        if (!isAbsenceInAcademicYear(a, historyFilters.academicYear)) {
          return false;
        }
      }

      // 2. Filter by staff name / national ID
      if (historyFilters.staffName.trim()) {
        const searchTerms = historyFilters.staffName.toLowerCase().split(',').map(t => t.trim()).filter(Boolean);
        const nameMatch = searchTerms.length === 0 || searchTerms.some(term => 
          (a.staffName || '').toLowerCase().includes(term) || (a.nationalId || '').includes(term)
        );
        if (!nameMatch) return false;
      }

      // 3. Custom start date filter (if provided by user)
      if (historyFilters.startDate && a.startDate < historyFilters.startDate) {
        return false;
      }

      // 4. Custom end date filter (if provided by user)
      if (historyFilters.endDate && (a.endDate || a.startDate) > historyFilters.endDate) {
        return false;
      }

      // 5. Certificate status filter
      if (historyFilters.certificateStatus === 'submitted' && !a.hasCertificate) {
        return false;
      }
      if (historyFilters.certificateStatus === 'pending' && a.hasCertificate) {
        return false;
      }

      return true;
    });
  }, [absences, historyFilters]);

  const historyStats = React.useMemo(() => {
    const total = filteredAbsences.length;
    const withCert = filteredAbsences.filter(a => a.hasCertificate).length;
    const pendingCert = total - withCert;
    return { total, withCert, pendingCert };
  }, [filteredAbsences]);

  useEffect(() => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    setIsWeekend(day === 5 || day === 6);
  }, [selectedDate]);

  const changeDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  useEffect(() => {
    fetchStaff();
    fetchReasons();
    fetchSubstitutes();
    
    const q = query(
      collection(db, 'staffAbsences'),
      where('staffType', '==', type),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAbsences(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as StaffAbsenceType[]);
    }, (error) => {
      if (isQuotaError(error)) {
        setMessage({ text: 'מכסת השימוש בבסיס הנתונים הסתיימה להיום.', type: 'error' });
        unsubscribe();
      }
      console.error("Error listening to absences:", error);
    });

    return () => unsubscribe();
  }, [type]);

  const fetchReasons = async (forceRefresh = false) => {
    try {
      const q = query(collection(db, 'absenceReasons'), orderBy('name'));
      const results = await getCachedQuery('absence_reasons', q, forceRefresh);
      const names = results.map((r: any) => r.name).filter(Boolean);
      if (names.length > 0) {
        setReasons(names);
      } else {
        setReasons(DEFAULT_ABSENCE_REASONS);
      }
    } catch (error) {
      console.error("Error fetching reasons:", error);
      setReasons(DEFAULT_ABSENCE_REASONS);
    }
  };

  const fetchSubstitutes = async (forceRefresh = false) => {
    try {
      // Fetch from staff collection
      const allStaff = await getCachedStaff(forceRefresh);
      const fromStaff = allStaff
        .filter(s => (s.isSubstitute || (s.role && s.role.includes('ממלא'))) && s.active !== false)
        .map(s => ({ 
          id: s.id, 
          name: s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() 
        }));

      // Fetch from substitutes collection (legacy/external)
      let fromSubs: any[] = [];
      try {
        const qSubs = query(collection(db, 'substitutes'), where('active', '==', true));
        fromSubs = await getCachedQuery('substitutes_active', qSubs, forceRefresh);
      } catch (e) {
        console.warn("Substitutes collection might not exist yet", e);
      }

      // Merge and unique
      const merged = [
        ...fromStaff, 
        ...fromSubs.map((s: any) => ({ 
          id: s.id, 
          name: s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() 
        }))
      ];
      
      // Filter out empty names and de-duplicate by name
      const unique = Array.from(new Map(
        merged
          .filter(item => item.name && item.name.length > 1)
          .map(item => [item.name, item])
      ).values());
      
      setSubstitutes(unique.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he')));
    } catch (error) {
      console.error("Error fetching substitutes:", error);
    }
  };

  const fetchStaff = async (forceRefresh = false) => {
    let safetyTimer: any = setTimeout(() => {
      setLoadingStaff(false);
    }, 5000);
    setLoadingStaff(true);
    try {
      const allStaff = await getCachedStaff(forceRefresh);
      const filtered = allStaff
        .filter(s => {
          if (s.active === false) return false;
          
          // Determine effective type for filtering
          const role = (s.role || '').trim();
          const isAssistantRole = role.includes('סייע') || role.includes('אם בית');
          
          let effectiveType = s.type;
          
          if (isAssistantRole) {
            effectiveType = 'assistant';
          } else if (!effectiveType) {
            effectiveType = 'educational';
          }
          
          return effectiveType === type;
        })
        .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'he'));
      setStaffList(filtered);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching staff:", error);
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
      setLoadingStaff(false);
    }
  };

  const handleRefresh = () => {
    fetchStaff(true);
    fetchReasons(true);
    fetchSubstitutes(true);
  };

  const handleStaffSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const staff = staffList.find(s => s.id === selectedId);
    if (staff) {
      setFormData(prev => ({
        ...prev,
        staffId: selectedId,
        staffName: staff.fullName,
        nationalId: staff.nationalId
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        staffId: '',
        staffName: '',
        nationalId: ''
      }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any pending auto-saves
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      setAutoSaving(false);
    }
    
    if (!formData.staffName || !formData.nationalId) {
      setMessage({ text: 'יש לבחור איש צוות', type: 'error' });
      return;
    }

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setMessage({ text: 'תאריך סיום לא יכול להיות לפני תאריך התחלה', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const yearForDate = getAcademicYearForDate(formData.startDate) || selectedYear;
      const dataToSave = {
        ...formData,
        academicYear: yearForDate,
        substituteHours: formData.substituteHours ? Number(formData.substituteHours) : null,
        staffType: type,
        timestamp: serverTimestamp()
      };

      if (formData.id) {
        const { id, ...updateData } = dataToSave;
        await updateDoc(doc(db, 'staffAbsences', id), updateData);
        setMessage({ text: 'הדיווח עודכן בהצלחה!', type: 'success' });
      } else {
        const { id, ...addData } = dataToSave;
        await addDoc(collection(db, 'staffAbsences'), addData);
        setMessage({ text: 'ההיעדרות נשמרה בהצלחה!', type: 'success' });
      }
      
      // Update daily stats for the affected range
      await refreshDailyStats(formData.startDate);
      if (formData.endDate !== formData.startDate) {
        await refreshDailyStats(formData.endDate);
      }
      
      // Sync to holiday attendance if applicable (batch processing)
      try {
        const holidays = await getHolidays();
        let currentDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);
        
        const { writeBatch } = await import('firebase/firestore');
        const batch = writeBatch(db);
        let syncCount = 0;

        while (currentDate <= endDate) {
          const dayOfWeek = currentDate.getDay();
          const isWeekendDay = dayOfWeek === 5 || dayOfWeek === 6;
          
          if (!isWeekendDay) {
            const dateStr = currentDate.toISOString().split('T')[0];
            const holiday = getHolidayForDate(dateStr, holidays);
            if (holiday) {
              const syncDocId = `${holiday.id}_${dateStr}_${formData.staffId}`;
              const syncDocRef = doc(db, 'holidayAttendance', syncDocId);
              batch.set(syncDocRef, {
                holidayId: holiday.id,
                date: dateStr,
                targetId: formData.staffId,
                targetType: type,
                status: 'absent',
                lastUpdated: serverTimestamp()
              }, { merge: true });
              syncCount++;
            }
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        if (syncCount > 0) {
          await batch.commit();
        }
      } catch (syncError) {
        if (isQuotaError(syncError)) {
          console.warn("Quota exceeded during holiday sync range (ignored)");
        } else {
          console.error("Error syncing to holiday attendance:", syncError);
        }
      }
      
      setFormData({
        id: '',
        staffId: '',
        staffName: '',
        nationalId: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        reason: '',
        substitute: '',
        substituteHours: '',
        notes: '',
        hasCertificate: false,
        certificateType: ''
      });
      setMessage({ text: 'ההיעדרות נשמרה בהצלחה! ייתכן וייקח מספר דקות עד שהשינוי יופיע בדשבורד.', type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      if (isQuotaError(error)) {
        setMessage({ text: 'מכסת הכתיבה הסתיימה להיום. הנתונים לא נשמרו.', type: 'error' });
      } else {
        handleFirestoreError(error, OperationType.WRITE, 'staffAbsences');
        setMessage({ text: 'שגיאה בשמירת הנתונים', type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCertificate = async (absenceId: string, hasCert: boolean, certType: string) => {
    try {
      await updateDoc(doc(db, 'staffAbsences', absenceId), {
        hasCertificate: hasCert,
        certificateType: certType
      });
      
      // Refresh stats in case this absence affects today
      const today = new Date().toISOString().split('T')[0];
      await refreshDailyStats(today);
    } catch (error) {
      console.error("Error updating certificate:", error);
    }
  };

  const handleDeleteAbsence = async (absenceId: string, skipConfirm = false) => {
    if (!absenceId) {
      console.warn("handleDeleteAbsence called without ID");
      return;
    }
    
    // Use state-based confirmation instead of window.confirm
    if (!skipConfirm && confirmDeleteId !== absenceId) {
      setConfirmDeleteId(absenceId);
      // Auto-reset after 3 seconds
      setTimeout(() => setConfirmDeleteId(null), 3000);
      return;
    }
    
    // Clear any pending auto-saves
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      setAutoSaving(false);
    }
    
    setConfirmDeleteId(null);
    setActionLoading(absenceId);
    try {
      console.log(`Starting deletion for: ${absenceId}`);
      const absenceToDelete = absences.find(a => a.id === absenceId);
      
      // Update local state immediately to ensure UI reacts even if onSnapshot is slow
      setAbsences(prev => prev.filter(a => a.id !== absenceId));

      // Perform the delete in Firestore
      await deleteDoc(doc(db, 'staffAbsences', absenceId));
      console.log(`Firestore document ${absenceId} deleted successfully`);
      
      // Update stats in background
      if (absenceToDelete) {
        refreshDailyStats(absenceToDelete.startDate).catch(e => console.error("Stats refresh error:", e));
        if (absenceToDelete.endDate !== absenceToDelete.startDate) {
          refreshDailyStats(absenceToDelete.endDate).catch(e => console.error("Stats refresh error:", e));
        }
      }
      
      // If we were editing this specific absence, reset the form and go back
      if (formData.id === absenceId) {
        setFormData({
          id: '',
          staffId: '',
          staffName: '',
          nationalId: '',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0],
          reason: '',
          substitute: '',
          substituteHours: '',
          notes: '',
          hasCertificate: false,
          certificateType: ''
        });
        // Stay in current view if possible, or go back to daily if in report mode
        if (viewMode === 'report') {
          setViewMode('daily');
        }
      }
      
      setMessage({ text: 'הדיווח נמחק בהצלחה', type: 'success' });
    } catch (error) {
      console.error("Critical error during deletion:", error);
      // Revert local state on error
      const q = query(
        collection(db, 'staffAbsences'),
        where('staffType', '==', type),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(q);
      setAbsences(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as StaffAbsenceType[]);

      try {
        handleFirestoreError(error, OperationType.DELETE, `staffAbsences/${absenceId}`);
      } catch (err) {
        const errMessage = err instanceof Error ? err.message : String(err);
        setMessage({ 
          text: `שגיאה במחיקת הדיווח: ${errMessage.includes('permission') ? 'אין הרשאה מתאימה' : 'שגיאת שרת'}`, 
          type: 'error' 
        });
      }
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    }
  };

  const certificateTypes = ['מחלה', 'הצהרה', 'חופשה', 'מילואים', 'אבל', 'אחר'];

  const exportToExcel = () => {
    const activeYearName = historyFilters.academicYear === 'all' 
      ? 'כל השנים' 
      : (academicYears.find(y => y.id === historyFilters.academicYear)?.name || historyFilters.academicYear);
    const range = historyFilters.academicYear !== 'all' ? getAcademicYearRange(historyFilters.academicYear) : null;
    const rangeText = range ? ` (${range.startDate} עד ${range.endDate})` : '';

    const data = filteredAbsences.map(a => ({
      'שם איש צוות': a.staffName,
      'תעודת זהות': a.nationalId,
      'שנת לימודים': a.academicYear || getAcademicYearForDate(a.startDate),
      'תאריך התחלה': a.startDate,
      'תאריך סיום': a.endDate,
      'סיבה': a.reason,
      'ממלא מקום': a.substitute || '-',
      'שעות ממלא מקום': a.substituteHours || '-',
      'אישור': a.hasCertificate ? `כן (${a.certificateType})` : 'לא',
      'הערות': a.notes || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "היסטוריית היעדרויות");
    XLSX.writeFile(wb, `absences_${type}_${historyFilters.academicYear || 'all'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToPDF = () => {
    const activeYearName = historyFilters.academicYear === 'all' 
      ? 'כל השנים' 
      : (academicYears.find(y => y.id === historyFilters.academicYear)?.name || historyFilters.academicYear);
    const range = historyFilters.academicYear !== 'all' ? getAcademicYearRange(historyFilters.academicYear) : null;

    const doc = new jsPDF('p', 'pt');
    
    const tableData = filteredAbsences.map(a => [
      a.notes || '-',
      a.hasCertificate ? `${a.certificateType}` : 'לא',
      a.substitute || '-',
      a.reason,
      `${a.startDate} - ${a.endDate}`,
      a.staffName
    ]);

    doc.setFontSize(16);
    doc.text(`דוח היעדרויות - ${type === 'educational' ? 'צוות חינוכי טיפולי' : 'צוות סייעות'}`, doc.internal.pageSize.width - 40, 30, { align: 'right' });
    doc.setFontSize(10);
    doc.text(`שנת לימודים: ${activeYearName}${range ? ` (${range.startDate} - ${range.endDate})` : ''}`, doc.internal.pageSize.width - 40, 48, { align: 'right' });

    autoTable(doc, {
      head: [['הערות', 'אישור', 'ממלא מקום', 'סיבה', 'תאריכים', 'שם איש צוות']],
      body: tableData,
      styles: { font: 'helvetica', halign: 'right' },
      headStyles: { fillColor: [79, 70, 229] },
      margin: { top: 60 }
    });

    doc.save(`absences_${type}_${historyFilters.academicYear || 'all'}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" dir="rtl">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">
            נוכחות {type === 'educational' ? 'צוות חינוכי טיפולי' : 'צוות סייעות'}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500">ניהול נוכחות, היעדרויות ואישורים</p>
            {lastUpdated && (
              <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                עודכן לאחרונה: {lastUpdated.toLocaleTimeString('he-IL')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleRefresh}
            disabled={loadingStaff}
            className="inline-flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-4 py-2 rounded-xl font-bold hover:bg-slate-50 transition-all shadow-sm disabled:opacity-50"
            title="רענן נתונים מהשרת"
          >
            <RefreshCw size={18} className={loadingStaff ? 'animate-spin' : ''} />
            <span>רענן</span>
          </button>
          <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm">
          <button
            onClick={() => setViewMode('daily')}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
              viewMode === 'daily' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <ClipboardCheck size={18} />
            <span>נוכחות יומית</span>
          </button>
          <button
            onClick={() => setViewMode('report')}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
              viewMode === 'report' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <UserX size={18} />
            <span>דיווח היעדרות</span>
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
              viewMode === 'history' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <History size={18} />
            <span>היסטוריה ואישורים</span>
          </button>
        </div>
      </div>
    </div>

    <AnimatePresence mode="wait">
        {viewMode === 'daily' && (
          <motion.div
            key="daily"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 text-emerald-600 rounded-2xl">
                  <ClipboardCheck size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold">נוכחות יומית - ברירת מחדל: נוכח</h3>
                  <p className="text-slate-500 text-sm">סמן רק את אנשי הצוות שנעדרו היום</p>
                </div>
              </div>
              <div className="flex items-center bg-slate-50 rounded-2xl border border-slate-200 p-1 shadow-sm">
                <button
                  onClick={() => changeDate(1)}
                  className="p-2 hover:bg-white hover:shadow-sm rounded-xl text-slate-600 transition-all"
                  title="יום הבא"
                >
                  <ChevronRight size={20} />
                </button>
                
                <div className="relative flex items-center px-4 border-x border-slate-200">
                  <CalendarIcon size={16} className="text-slate-400 ml-2" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="bg-transparent border-none text-sm font-bold text-slate-700 focus:ring-0 p-0 cursor-pointer"
                  />
                </div>

                <button
                  onClick={() => changeDate(-1)}
                  className="p-2 hover:bg-white hover:shadow-sm rounded-xl text-slate-600 transition-all"
                  title="יום קודם"
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
            </div>

            {isWeekend && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3 text-amber-700">
                <AlertTriangle size={20} className="shrink-0" />
                <p className="text-sm font-bold">שים לב: התאריך הנבחר הוא יום שישי או שבת. בית הספר אינו עובד בימים אלו.</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Absent Staff Column (Right) */}
              <div className="space-y-4">
                <h4 className="font-bold text-red-600 flex items-center gap-2 mb-4">
                  <UserX size={20} />
                  נעדרים ({staffList.filter(s => absences.some(a => a.staffId === s.id && selectedDate >= a.startDate && selectedDate <= a.endDate)).length})
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  {staffList
                    .filter(staff => absences.some(a => a.staffId === staff.id && selectedDate >= a.startDate && selectedDate <= a.endDate))
                    .map(staff => {
                      const absence = absences.find(a => a.staffId === staff.id && selectedDate >= a.startDate && selectedDate <= a.endDate);
                      return (
                        <div 
                          key={staff.id}
                          onClick={() => toggleDayAbsence(staff)}
                          className="p-4 rounded-2xl border bg-red-50 border-red-200 shadow-sm transition-all flex items-center justify-between cursor-pointer hover:shadow-md"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-100 text-red-600">
                              <UserX size={20} />
                            </div>
                            <div>
                              <p className="font-bold text-slate-900">{staff.fullName}</p>
                              <p className="text-xs text-red-600 font-medium">{absence?.reason}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (absence) {
                                  setFormData({
                                    id: absence.id || '',
                                    staffId: absence.staffId,
                                    staffName: absence.staffName,
                                    nationalId: absence.nationalId,
                                    startDate: absence.startDate,
                                    endDate: absence.endDate,
                                    reason: absence.reason,
                                    substitute: absence.substitute,
                                    substituteHours: absence.substituteHours?.toString() || '',
                                    notes: absence.notes,
                                    hasCertificate: !!absence.hasCertificate,
                                    certificateType: absence.certificateType || ''
                                  });
                                  setViewMode('report');
                                }
                              }}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                            >
                              ערוך
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (absence) handleDeleteAbsence(absence.id!);
                              }}
                              disabled={actionLoading === absence?.id}
                              className={`p-2 rounded-lg transition-all flex items-center justify-center min-w-[40px] ${
                                confirmDeleteId === absence?.id 
                                  ? 'bg-red-600 text-white animate-pulse' 
                                  : 'bg-red-100 text-red-600 hover:bg-red-200'
                              } ${actionLoading === absence?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={confirmDeleteId === absence?.id ? 'לחץ שוב למחיקה סופית' : 'מחק דיווח'}
                            >
                              {actionLoading === absence?.id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : confirmDeleteId === absence?.id ? (
                                <span className="text-[10px] font-bold">בטוח?</span>
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  {staffList.filter(s => absences.some(a => a.staffId === s.id && selectedDate >= a.startDate && selectedDate <= a.endDate)).length === 0 && (
                    <div className="p-8 text-center border-2 border-dashed border-slate-100 rounded-3xl text-slate-400">
                      אין נעדרים היום
                    </div>
                  )}
                </div>
              </div>

              {/* Present Staff Column (Left) */}
              <div className="space-y-4">
                <h4 className="font-bold text-emerald-600 flex items-center gap-2 mb-4">
                  <UserCheck size={20} />
                  נוכחים ({staffList.filter(s => !absences.some(a => a.staffId === s.id && selectedDate >= a.startDate && selectedDate <= a.endDate)).length})
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {staffList
                    .filter(staff => !absences.some(a => a.staffId === staff.id && selectedDate >= a.startDate && selectedDate <= a.endDate))
                    .map(staff => (
                      <div 
                        key={staff.id}
                        onClick={() => toggleDayAbsence(staff)}
                        className="p-3 rounded-xl border border-slate-100 bg-white hover:border-emerald-200 transition-all flex items-center justify-between cursor-pointer group"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 transition-colors">
                            <UserCheck size={16} />
                          </div>
                          <p className="text-sm font-bold text-slate-700">{staff.fullName}</p>
                        </div>
                        <div className="text-[10px] font-bold text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          דווח היעדרות
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {viewMode === 'report' && (
          <motion.div
            key="report"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-100 text-indigo-600 rounded-2xl">
                  <UserX size={24} />
                </div>
                <h3 className="text-xl font-bold">דיווח היעדרות</h3>
              </div>
              {autoSaving && (
                <div className="flex items-center gap-2 text-indigo-600 text-xs font-medium animate-pulse">
                  <Loader2 size={14} className="animate-spin" />
                  שומר אוטומטית...
                </div>
              )}
            </div>

            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">בחר איש צוות</label>
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <select
                      required
                      value={formData.staffId}
                      onChange={(e) => { handleStaffSelect(e); triggerAutoSave(); }}
                      disabled={loadingStaff}
                      className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                    >
                      <option value="">{loadingStaff ? 'טוען רשימה...' : 'בחר מהרשימה...'}</option>
                      {staffList.map(s => (
                        <option key={s.id} value={s.id}>{s.fullName} ({s.nationalId})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">תאריך התחלה</label>
                  <div className="relative">
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="date"
                      name="startDate"
                      required
                      value={formData.startDate}
                      onChange={(e) => { handleChange(e); triggerAutoSave(); }}
                      className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">תאריך סיום</label>
                  <div className="relative">
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="date"
                      name="endDate"
                      required
                      value={formData.endDate}
                      onChange={(e) => { handleChange(e); triggerAutoSave(); }}
                      className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">סיבת היעדרות</label>
                  <select
                    name="reason"
                    required
                    value={formData.reason}
                    onChange={(e) => { handleChange(e); triggerAutoSave(); }}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  >
                    <option value="">בחר סיבה...</option>
                    {reasons.map(reason => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                    <option value="אחר">אחר</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">מילוי מקום</label>
                  <select
                    name="substitute"
                    value={formData.substitute}
                    onChange={(e) => { handleChange(e); triggerAutoSave(); }}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                  >
                    <option value="">בחר ממלא מקום (אם יש)...</option>
                    {substitutes.map(sub => (
                      <option key={sub.id} value={sub.name}>{sub.name}</option>
                    ))}
                    <option value="אחר">אחר (הזן בהערות)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">שעות עבודה ממלא מקום</label>
                  <input
                    type="number"
                    name="substituteHours"
                    step="0.5"
                    min="0"
                    value={formData.substituteHours}
                    onChange={(e) => { handleChange(e); triggerAutoSave(); }}
                    placeholder="מספר שעות..."
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="p-6 bg-indigo-50 rounded-2xl border border-indigo-100 space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hasCertificate"
                    name="hasCertificate"
                    checked={formData.hasCertificate}
                    onChange={(e) => { 
                      setFormData(prev => ({ ...prev, hasCertificate: e.target.checked }));
                      triggerAutoSave();
                    }}
                    className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="hasCertificate" className="font-bold text-indigo-900 cursor-pointer flex items-center gap-2">
                    <FileCheck size={18} />
                    הביא אישור?
                  </label>
                </div>

                {formData.hasCertificate && (
                  <div className="animate-in fade-in slide-in-from-top-2">
                    <label className="block text-sm font-medium text-indigo-700 mb-2">סוג אישור</label>
                    <div className="flex flex-wrap gap-2">
                      {certificateTypes.map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => { 
                            setFormData(prev => ({ ...prev, certificateType: type }));
                            triggerAutoSave();
                          }}
                          className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                            formData.certificateType === type
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">הערות</label>
                <div className="relative">
                  <FileText className="absolute right-3 top-3 text-slate-400" size={18} />
                  <textarea
                    name="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => { handleChange(e); triggerAutoSave(); }}
                    className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all resize-none"
                    placeholder="הערות נוספות..."
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 pt-4 border-t border-slate-100">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        id: '',
                        staffId: '',
                        staffName: '',
                        nationalId: '',
                        startDate: new Date().toISOString().split('T')[0],
                        endDate: new Date().toISOString().split('T')[0],
                        reason: '',
                        substitute: '',
                        substituteHours: '',
                        notes: '',
                        hasCertificate: false,
                        certificateType: ''
                      });
                      setViewMode('daily');
                    }}
                    className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all"
                  >
                    סגור וחזור
                  </button>
                  
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e as any)}
                    disabled={saving}
                    className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    <span>שמור דיווח</span>
                  </button>
                  
                  {formData.id && (
                    <button
                      type="button"
                      onClick={() => handleDeleteAbsence(formData.id)}
                      disabled={actionLoading === formData.id}
                      className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 border disabled:opacity-50 ${
                        confirmDeleteId === formData.id
                          ? 'bg-red-600 text-white border-red-600 animate-pulse'
                          : 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100'
                      }`}
                    >
                      {actionLoading === formData.id ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : confirmDeleteId === formData.id ? (
                        <XCircle size={18} />
                      ) : (
                        <Trash2 size={18} />
                      )}
                      <span>{confirmDeleteId === formData.id ? 'בטוח שברצונך למחוק?' : 'מחק דיווח זה'}</span>
                    </button>
                  )}
                </div>

                <div className="text-xs text-slate-400 font-medium italic">
                  * הנתונים נשמרים באופן אוטומטי בעת מילוי הטופס
                </div>
              </div>
            </form>
          </motion.div>
        )}

        {viewMode === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden"
          >
            <div className="p-6 border-b border-slate-100 bg-slate-50/50 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <History size={24} className="text-indigo-600" />
                    היסטוריית היעדרויות וניהול אישורים
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    מוגדר לפי שנת לימודים (מ-1.9 לשנה ועד 31.8 לשנה העוקבת)
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={exportToExcel}
                    className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 border border-emerald-200 px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-emerald-100 transition-all shadow-sm"
                  >
                    <FileDown size={16} />
                    <span>ייצוא ל-EXCEL</span>
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="inline-flex items-center gap-2 bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-red-100 transition-all shadow-sm"
                  >
                    <FileTextIcon size={16} />
                    <span>ייצוא ל-PDF</span>
                  </button>
                </div>
              </div>

              {/* Academic Year Selection & Range Notice */}
              <div className="p-4 bg-indigo-50/80 border border-indigo-100 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-sm">
                    <GraduationCap size={22} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-indigo-950">שנת לימודים מוצגת:</span>
                      <span className="text-xs font-black text-indigo-700 bg-white px-2.5 py-0.5 rounded-full border border-indigo-200 shadow-xs">
                        {historyFilters.academicYear === 'all' 
                          ? 'כל השנים (היסטוריה מלאה)' 
                          : (academicYears.find(y => y.id === historyFilters.academicYear)?.name || historyFilters.academicYear)}
                      </span>
                    </div>
                    {historyFilters.academicYear !== 'all' && (
                      <p className="text-[11px] text-indigo-700 font-medium mt-1">
                        הגדרה רשמית: {getAcademicYearRange(historyFilters.academicYear).startDate} עד {getAcademicYearRange(historyFilters.academicYear).endDate} (1.9 עד 31.8)
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 self-start md:self-auto">
                  <label className="text-xs font-bold text-slate-700 whitespace-nowrap">בחר שנה להצגה:</label>
                  <select
                    value={historyFilters.academicYear}
                    onChange={(e) => {
                      const newYear = e.target.value;
                      setHistoryFilters(prev => ({ ...prev, academicYear: newYear }));
                      if (newYear !== 'all') {
                        setSelectedYear(newYear);
                      }
                    }}
                    className="text-xs font-bold bg-white text-slate-800 border border-indigo-200 rounded-xl px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 shadow-sm outline-none cursor-pointer"
                  >
                    {academicYears.map(y => (
                      <option key={y.id} value={y.id}>
                        {y.name} (01/09 - 31/08)
                      </option>
                    ))}
                    <option value="all">כל השנים (היסטוריה מלאה)</option>
                  </select>
                </div>
              </div>

              {/* Stats overview pills */}
              <div className="grid grid-cols-3 gap-3 pt-1">
                <div className="bg-white p-3 rounded-2xl border border-slate-200 text-center shadow-xs">
                  <div className="text-[11px] text-slate-500 font-medium">סה״כ היעדרויות</div>
                  <div className="text-lg font-black text-slate-800 mt-0.5">{historyStats.total}</div>
                </div>
                <div className="bg-emerald-50/50 p-3 rounded-2xl border border-emerald-100 text-center shadow-xs">
                  <div className="text-[11px] text-emerald-700 font-medium">הוגש אישור</div>
                  <div className="text-lg font-black text-emerald-600 mt-0.5">{historyStats.withCert}</div>
                </div>
                <div className="bg-amber-50/50 p-3 rounded-2xl border border-amber-100 text-center shadow-xs">
                  <div className="text-[11px] text-amber-700 font-medium">טרם הוגש אישור</div>
                  <div className="text-lg font-black text-amber-600 mt-0.5">{historyStats.pendingCert}</div>
                </div>
              </div>

              {/* Search & date filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-1">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="חיפוש לפי שם או ת.ז..."
                    value={historyFilters.staffName}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, staffName: e.target.value }))}
                    className="w-full pr-10 pl-4 py-2 bg-white border border-slate-200 rounded-xl text-xs md:text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <select
                    value={historyFilters.certificateStatus}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, certificateStatus: e.target.value as any }))}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs md:text-sm focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                  >
                    <option value="all">כל האישורים</option>
                    <option value="submitted">הוגש אישור בלבד</option>
                    <option value="pending">טרם הוגש אישור</option>
                  </select>
                </div>
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="date"
                    value={historyFilters.startDate}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full pr-10 pl-4 py-2 bg-white border border-slate-200 rounded-xl text-xs md:text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="מתאריך..."
                  />
                </div>
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="date"
                    value={historyFilters.endDate}
                    onChange={(e) => setHistoryFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full pr-10 pl-4 py-2 bg-white border border-slate-200 rounded-xl text-xs md:text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="עד תאריך..."
                  />
                </div>
              </div>

              {(historyFilters.staffName || historyFilters.startDate || historyFilters.endDate || historyFilters.certificateStatus !== 'all' || historyFilters.academicYear !== selectedYear) && (
                <div className="flex justify-end pt-1">
                  <button
                    onClick={() => setHistoryFilters({ academicYear: selectedYear, staffName: '', startDate: '', endDate: '', certificateStatus: 'all' })}
                    className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-bold"
                  >
                    אפס סינונים וחזור לשנת {currentYearObj.name}
                  </button>
                </div>
              )}
            </div>
            
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-right">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-6 py-4 font-bold text-slate-700 text-sm">איש צוות</th>
                    <th className="px-6 py-4 font-bold text-slate-700 text-sm">תאריכים</th>
                    <th className="px-6 py-4 font-bold text-slate-700 text-sm">סיבה ומילוי מקום</th>
                    <th className="px-6 py-4 font-bold text-slate-700 text-sm">אישור רפואי/אחר</th>
                    <th className="px-6 py-4 font-bold text-slate-700 text-sm">פעולות</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredAbsences.map((absence) => (
                    <tr key={absence.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-bold text-slate-900">{absence.staffName}</p>
                        <p className="text-xs text-slate-500">{absence.nationalId}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar size={14} />
                          <span>{absence.startDate} - {absence.endDate}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-bold">
                            {absence.reason}
                          </span>
                          {absence.substitute && (
                            <p className="text-xs text-slate-500">
                              מ"מ: {absence.substitute} 
                              {absence.substituteHours ? ` (${absence.substituteHours} שעות)` : ''}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {absence.hasCertificate ? (
                          <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                            <CheckCircle2 size={18} />
                            <span>הוגש: {absence.certificateType}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-amber-600 font-bold text-sm">
                            <XCircle size={18} />
                            <span>טרם הוגש</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            {!absence.hasCertificate ? (
                              <div className="flex gap-1">
                                {certificateTypes.slice(0, 3).map(type => (
                                  <button
                                    key={type}
                                    onClick={() => handleUpdateCertificate(absence.id!, true, type)}
                                    className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-bold hover:bg-indigo-100 transition-all"
                                  >
                                    סמן {type}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleUpdateCertificate(absence.id!, false, '')}
                                className="text-slate-400 hover:text-red-600 text-xs font-bold"
                              >
                                ביטול אישור
                              </button>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setFormData({
                                id: absence.id || '',
                                staffId: absence.staffId,
                                staffName: absence.staffName,
                                nationalId: absence.nationalId,
                                startDate: absence.startDate,
                                endDate: absence.endDate,
                                reason: absence.reason,
                                substitute: absence.substitute,
                                substituteHours: absence.substituteHours?.toString() || '',
                                notes: absence.notes,
                                hasCertificate: !!absence.hasCertificate,
                                certificateType: absence.certificateType || ''
                              });
                              setViewMode('report');
                            }}
                            className="text-indigo-600 hover:text-indigo-800 text-xs font-bold text-right"
                          >
                            ערוך דיווח
                          </button>
                          <button
                            onClick={() => handleDeleteAbsence(absence.id!)}
                            disabled={actionLoading === absence.id}
                            className="text-red-400 hover:text-red-600 text-[10px] font-bold text-right flex items-center gap-1 disabled:opacity-50"
                          >
                            {actionLoading === absence.id ? <Loader2 size={10} className="animate-spin" /> : null}
                            מחק דיווח
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredAbsences.length === 0 && (
                <div className="p-12 text-center text-slate-400">
                  <div className="w-14 h-14 mx-auto mb-3 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400">
                    <UserCheck size={28} />
                  </div>
                  <p className="font-bold text-base text-slate-700">אין היעדרויות עבור הסינון הנבחר</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {historyFilters.academicYear !== 'all' 
                      ? `בשנת הלימודים ${academicYears.find(y => y.id === historyFilters.academicYear)?.name || historyFilters.academicYear} (1.9 עד 31.8) לא נמצאו רשומות היעדרות.`
                      : 'לא נמצאו דיווחי היעדרות התואמים לקריטריונים שהוגדרו.'}
                  </p>
                  {historyFilters.academicYear !== 'all' && (
                    <button
                      onClick={() => setHistoryFilters(prev => ({ ...prev, academicYear: 'all' }))}
                      className="mt-4 px-4 py-2 text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-colors"
                    >
                      הצג היעדרויות מכל השנים
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Card List */}
            <div className="md:hidden divide-y divide-slate-100">
              {filteredAbsences.map((absence) => (
                <div key={absence.id} className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-slate-900">{absence.staffName}</p>
                      <p className="text-xs text-slate-500">{absence.nationalId}</p>
                    </div>
                    <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-lg text-[10px] font-bold">
                      {absence.reason}
                    </span>
                  </div>
                  
                  {absence.substitute && (
                    <p className="text-xs text-slate-600">
                      מ"מ: {absence.substitute}
                      {absence.substituteHours ? ` (${absence.substituteHours} שעות)` : ''}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Calendar size={14} />
                    <span>{absence.startDate} - {absence.endDate}</span>
                  </div>

                  <div className="pt-2 border-t border-slate-50 flex items-center justify-between">
                    <div className="flex gap-4">
                      <button
                        onClick={() => {
                          setFormData({
                            id: absence.id || '',
                            staffId: absence.staffId,
                            staffName: absence.staffName,
                            nationalId: absence.nationalId,
                            startDate: absence.startDate,
                            endDate: absence.endDate,
                            reason: absence.reason,
                            substitute: absence.substitute,
                            substituteHours: absence.substituteHours?.toString() || '',
                            notes: absence.notes,
                            hasCertificate: !!absence.hasCertificate,
                            certificateType: absence.certificateType || ''
                          });
                          setViewMode('report');
                        }}
                        className="text-indigo-600 text-xs font-bold"
                      >
                        ערוך
                      </button>
                      <button
                        onClick={() => handleDeleteAbsence(absence.id!)}
                        disabled={actionLoading === absence.id}
                        className="text-red-500 text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                      >
                        {actionLoading === absence.id ? <Loader2 size={12} className="animate-spin" /> : null}
                        מחק
                      </button>
                    </div>
                    
                    {absence.hasCertificate ? (
                      <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs">
                        <CheckCircle2 size={16} />
                        <span>הוגש: {absence.certificateType}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-amber-600 font-bold text-xs">
                        <XCircle size={16} />
                        <span>טרם הוגש אישור</span>
                      </div>
                    )}
                  </div>
                  
                  {!absence.hasCertificate && (
                    <div className="flex flex-wrap gap-1">
                      {certificateTypes.map(type => (
                        <button
                          key={type}
                          onClick={() => handleUpdateCertificate(absence.id!, true, type)}
                          className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-bold"
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {filteredAbsences.length === 0 && (
                <div className="p-8 text-center text-slate-400">
                  <div className="w-12 h-12 mx-auto mb-2 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400">
                    <UserCheck size={24} />
                  </div>
                  <p className="font-bold text-sm text-slate-700">אין היעדרויות עבור הסינון הנבחר</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {historyFilters.academicYear !== 'all' 
                      ? `בשנת הלימודים ${academicYears.find(y => y.id === historyFilters.academicYear)?.name || historyFilters.academicYear} (1.9 עד 31.8) לא נמצאו רשומות היעדרות.`
                      : 'לא נמצאו דיווחי היעדרות.'}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {message.text && (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-2xl shadow-xl text-white font-bold z-50 ${
          message.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
        }`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default StaffAbsence;
